import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { Read } from 'services/financing-eproposta';
import { Title } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingEproposals() {
  const { user, filterExpression } = useContext(CoreContext);
  const [fetching, setFetching] = useState(false);
  const [registers, setRegisters] = useState([]);

  const history = useHistory();

  const rows = useMemo(() => {
    if (!registers) {
      return [];
    }
    const mapRows = (item) => ({
      id: item?.id,
      date: item?.createdAt,
      cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
      distributor: normalizeName(item?.distribuidor?.trade_name),
      integrator: !item?.nome_integrador
        ? !item?.pre_proposal?.nome_integrador
          ? item?.informacoes_projeto?.empresa_vendedor_cnpj
          : item?.pre_proposal?.nome_integrador
        : item?.nome_integrador,
      customer: normalizeName(item?.informacoes_cliente?.nome_completo),
      value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
      UC: item?.informacoes_projeto?.unidade_consumidora,
      city: item?.endereco_instalacao?.cidade,
      uf: item?.endereco_instalacao?.estado,
      dealership: item?.informacoes_projeto?.concessionaria,
      model_brand: item?.informacoes_projeto?.marca_placas,
      inverter_brand: item?.informacoes_projeto?.marca_inversores,
      modules: item?.informacoes_projeto?.quantidade_placas,
      power_kwp: `Potência kWp ${item?.informacoes_projeto?.potencia_kWp}`,
      kwh_month: `kWh / Mês ${parseInt(item?.informacoes_projeto?.geracao_energia_estimada / 12)}`,
      cliente_Id: item?.informacoes_cliente?.cpf,
      distribuidor: item?.distribuidor,
      espelho_pdf: item?.espelho_pdf?.[0]?.url,
      id_pre_proposal: item?.pre_proposal?.id,
    });

    if (user?.administrador) {
      return registers.map(mapRows);
    }
    const accessibleDistributorIds = [];
    if (user?.distribuidor?.id) {
      accessibleDistributorIds.push(user.distribuidor.id);
    }
    if (user?.distribuidores?.length > 0) {
      user.distribuidores.forEach((dist) =>
        accessibleDistributorIds.push(dist.id)
      );
    }
    const filteredRegisters = registers.filter((item) =>
      accessibleDistributorIds.includes(item?.distribuidor?.id)
    );
    return filteredRegisters.map(mapRows);
  }, [registers, user]);

  const init = useCallback(async () => {
    setFetching(true);

    try {
      const result = await Read('?filters[status]=pendente');
      //console.log(result);
      // if (!result.length) {
      //   setRegisters([]);
      //   setFetching(false);
      //   return;
      // }

      if (result && !exposeStrapiError(result)) {
        const nresult = result?.results?.sort((a, b) => b.id - a.id);
        setRegisters(nresult);
      }
    } catch (error) {
      console.error('Erro ao inicializar dados:', error);
      toast.error('Erro ao carregar propostas.');
    } finally {
      setFetching(false);
    }
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      {
        title: 'Tempo',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 20 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor (R$)', ref: 'value' },
      {
        title: 'TAG',
        ref: 'tag',
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => {
                if (!row.id_pre_proposal) {
                  toast.error('Proposta sem pré-proposta vinculada.');
                  return;
                }

                setRegisters([]);
                history.push(
                  `/proposals/financing-nova/${row?.id_pre_proposal}`
                );
              }}
              color={'orange'}
              nospace
              mbutton
              small
            >
              Validar Proposta
            </Button>
          </div>
        ),
      },

      // Inserir coluna "Espelho da Proposta" e "Observações" condicionalmente
      // removido por enquanto, tratar para o espelho trazer as informações do e-proposal e o campo obersavações o status do eproposta.
      // ...(user?.administrador
      //   ? [
      //       {
      //         title: 'Espelho da Proposta',
      //         ref: 'espelho',
      //         renderCell: ({ row }) => (
      //           <div style={{ display: 'flex', justifyContent: 'center' }}>
      //             <Button
      //               onClick={() => navigate(`proposals/financing/${row?.id}`)}
      //               color={'blue'}
      //               nospace
      //               mbutton
      //               small
      //             >
      //               Visualizar
      //             </Button>
      //           </div>
      //         ),
      //       },
      //       {
      //         title: 'Observações',
      //         renderCell: ({ row }) => (
      //           <Button
      //             onClick={() => {
      //               setModal({
      //                 type: 'observacao',
      //                 dados: row,
      //                 origem: 'Elegíveis',
      //                 reload: () => init(),
      //               });
      //             }}
      //             color="botao"
      //             nospace
      //             mbutton
      //             small
      //           >
      //             Adicionar
      //           </Button>
      //         ),
      //       },
      //     ]
      //   : []),
    ];
    return baseColumns;
  }, [user, history]);

  useEffect(() => init(), [init]);

  return (
    <ContainerAuthenticated>
      <Title>Propostas</Title>
      <TableWrapper title={'E-Propostas'} search>
        <BasicTable
          loading={fetching}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
